import { useState, useEffect, useMemo, useCallback } from 'react';
import { supabaseClient } from '../../config/supabase';

function AccountSettingsView({ accountSettings, setAccountSettings, triggerAutosave, userId }) {
    const [activeTab, setActiveTab] = useState('services');
    const [editingService, setEditingService] = useState(null);
    const [showServiceModal, setShowServiceModal] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [showProductModal, setShowProductModal] = useState(false);
    
    // Service management functions
    const addService = (service) => {
        const newService = {
            ...service,
            id: Math.max(...accountSettings.services.map(s => s.id), 0) + 1
        };
        setAccountSettings({
            ...accountSettings,
            services: [...accountSettings.services, newService]
        });
        if (triggerAutosave) triggerAutosave();
    };
    
    const updateService = (updatedService) => {
        setAccountSettings({
            ...accountSettings,
            services: accountSettings.services.map(s => s.id === updatedService.id ? updatedService : s)
        });
        if (triggerAutosave) triggerAutosave();
    };
    
    const deleteService = (serviceId) => {
        if (window.confirm('Are you sure you want to delete this service?')) {
            setAccountSettings({
                ...accountSettings,
                services: accountSettings.services.filter(s => s.id !== serviceId)
            });
            if (triggerAutosave) triggerAutosave();
        }
    };
    
    // Product CRUD Functions
    const addProduct = (product) => {
        const newProduct = {
            ...product,
            id: Math.max(...accountSettings.products.map(p => p.id), 0) + 1
        };
        setAccountSettings({
            ...accountSettings,
            products: [...accountSettings.products, newProduct]
        });
        if (triggerAutosave) triggerAutosave();
    };
    
    const updateProduct = (updatedProduct) => {
        setAccountSettings({
            ...accountSettings,
            products: accountSettings.products.map(p => p.id === updatedProduct.id ? updatedProduct : p)
        });
        if (triggerAutosave) triggerAutosave();
    };
    
    const deleteProduct = (productId) => {
        if (window.confirm('Are you sure you want to delete this product?')) {
            setAccountSettings({
                ...accountSettings,
                products: accountSettings.products.filter(p => p.id !== productId)
            });
            if (triggerAutosave) triggerAutosave();
        }
    };
    
    // Template CRUD Functions
    const [editingTemplate, setEditingTemplate] = useState(null);
    const [showTemplateModal, setShowTemplateModal] = useState(false);
    
    const addTemplate = (template) => {
        const newTemplate = {
            ...template,
            id: Math.max(...(accountSettings.appointmentTemplates || []).map(t => t.id), 0) + 1
        };
        setAccountSettings({
            ...accountSettings,
            appointmentTemplates: [...(accountSettings.appointmentTemplates || []), newTemplate]
        });
        if (triggerAutosave) triggerAutosave();
    };
    
    const updateTemplate = (updatedTemplate) => {
        setAccountSettings({
            ...accountSettings,
            appointmentTemplates: accountSettings.appointmentTemplates.map(t => t.id === updatedTemplate.id ? updatedTemplate : t)
        });
        if (triggerAutosave) triggerAutosave();
    };
    
    const deleteTemplate = (templateId) => {
        if (window.confirm('Are you sure you want to delete this template?')) {
            setAccountSettings({
                ...accountSettings,
                appointmentTemplates: accountSettings.appointmentTemplates.filter(t => t.id !== templateId)
            });
            if (triggerAutosave) triggerAutosave();
        }
    };
    
    // Calendar settings update
    const updateCalendarSettings = (updates) => {
        setAccountSettings({
            ...accountSettings,
            calendar: { ...accountSettings.calendar, ...updates }
        });
        if (triggerAutosave) triggerAutosave();
    };

    return (
        <div className="h-full flex flex-col">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 p-4">
                <div className="flex items-center gap-4 mb-4">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-800">Account Settings</h2>
                        <p className="text-sm text-gray-600 mt-1">Configure staff, services, and calendar preferences. Your word for a booking is in Admin Center › Company Words.</p>
                    </div>
                </div>
                
                {/* Tab Navigation */}
                <div className="flex gap-2 border-b border-gray-200">
                    <button
                        onClick={() => setActiveTab('services')}
                        className={`px-4 py-2 font-medium transition ${
                            activeTab === 'services'
                                ? 'text-[var(--accent-ink)] border-b-2 border-[var(--accent-ink)]'
                                : 'text-gray-600 hover:text-gray-800'
                        }`}
                    >
                        Services
                    </button>
                    <button
                        onClick={() => setActiveTab('products')}
                        className={`px-4 py-2 font-medium transition ${
                            activeTab === 'products'
                                ? 'text-[var(--accent-ink)] border-b-2 border-[var(--accent-ink)]'
                                : 'text-gray-600 hover:text-gray-800'
                        }`}
                    >
                        Products
                    </button>
                    <button
                        onClick={() => setActiveTab('templates')}
                        className={`px-4 py-2 font-medium transition ${
                            activeTab === 'templates'
                                ? 'text-[var(--accent-ink)] border-b-2 border-[var(--accent-ink)]'
                                : 'text-gray-600 hover:text-gray-800'
                        }`}
                    >
                        Templates ⭐
                    </button>
                    <button
                        onClick={() => setActiveTab('calendar')}
                        className={`px-4 py-2 font-medium transition ${
                            activeTab === 'calendar'
                                ? 'text-[var(--accent-ink)] border-b-2 border-[var(--accent-ink)]'
                                : 'text-gray-600 hover:text-gray-800'
                        }`}
                    >
                        Calendar
                    </button>
                </div>
            </div>
            
            {/* Tab Content */}
            <div className="flex-1 overflow-auto p-6">
                {/* Services Tab - Full CRUD Implementation */}
                {activeTab === 'services' && (
                    <div>
                        <div className="flex justify-between items-center mb-6">
                            <div>
                                <h3 className="text-2xl font-bold text-gray-800">Services</h3>
                                <p className="text-sm text-gray-600 mt-1">Manage services offered to clients</p>
                            </div>
                            <button
                                onClick={() => {
                                    setEditingService(null);
                                    setShowServiceModal(true);
                                }}
                                className="px-4 py-2 bg-[var(--btn-fill)] text-[var(--btn-ink)] rounded-lg hover:bg-[var(--btn-fill-hover)] font-medium flex items-center gap-2"
                            >
                                <span>+</span> Add Service
                            </button>
                        </div>
                        
                        {/* Services List */}
                        <div className="bg-white rounded-lg shadow overflow-hidden">
                            <table className="w-full">
                                <thead className="bg-gray-50 border-b">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Name</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Category</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Price</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Duration</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Vehicle</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Status</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {accountSettings.services.map(service => (
                                        <tr key={service.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4">
                                                <div className="font-medium text-gray-900">{service.name}</div>
                                                <div className="text-sm text-gray-500 truncate max-w-xs">{service.description}</div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className="text-sm text-gray-600">{service.category}</span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className="font-medium text-gray-900">${service.price}</span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className="text-sm text-gray-600">{service.duration} min</span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className="text-sm text-gray-600">{service.vehicleType}</span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className={`px-2 py-1 text-xs font-medium rounded ${
                                                    service.active 
                                                        ? 'bg-green-100 text-green-800' 
                                                        : 'bg-red-100 text-red-800'
                                                }`}>
                                                    {service.active ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex gap-2">
                                                    <button
                                                        onClick={() => {
                                                            setEditingService(service);
                                                            setShowServiceModal(true);
                                                        }}
                                                        className="text-blue-600 hover:text-blue-800 font-medium text-sm"
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        onClick={() => deleteService(service.id)}
                                                        className="text-red-600 hover:text-red-800 font-medium text-sm"
                                                    >
                                                        Delete
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            
                            {accountSettings.services.length === 0 && (
                                <div className="text-center py-12">
                                    <p className="text-gray-500">No services yet. Click "Add Service" to get started.</p>
                                </div>
                            )}
                        </div>
                        
                        {/* Add/Edit Service Modal */}
                        {showServiceModal && (
                            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowServiceModal(false)}>
                                <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                                    <div className="p-6">
                                        <h3 className="text-xl font-bold text-gray-800 mb-4">
                                            {editingService ? 'Edit Service' : 'Add New Service'}
                                        </h3>
                                        
                                        <form onSubmit={(e) => {
                                            e.preventDefault();
                                            const formData = new FormData(e.target);
                                            const serviceData = {
                                                name: formData.get('name'),
                                                category: formData.get('category'),
                                                price: parseFloat(formData.get('price')),
                                                duration: parseInt(formData.get('duration')),
                                                description: formData.get('description'),
                                                vehicleType: formData.get('vehicleType'),
                                                additionalInfo: formData.get('additionalInfo'),
                                                active: formData.get('active') === 'on'
                                            };
                                            
                                            if (editingService) {
                                                updateService({ ...editingService, ...serviceData });
                                            } else {
                                                addService(serviceData);
                                            }
                                            setShowServiceModal(false);
                                            setEditingService(null);
                                        }}>
                                            <div className="space-y-4">
                                                {/* Name & Category */}
                                                <div className="grid grid-cols-2 gap-4">
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                                            Service Name <span className="required-asterisk">*</span>
                                                        </label>
                                                        <input
                                                            type="text"
                                                            name="name"
                                                            defaultValue={editingService?.name || ''}
                                                            required
                                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent-ink)]"
                                                            placeholder="e.g., Airport Transfer - Zone 1"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                                            Category
                                                        </label>
                                                        <input
                                                            type="text"
                                                            name="category"
                                                            defaultValue={editingService?.category || ''}
                                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent-ink)]"
                                                            placeholder="e.g., Airport Transfers"
                                                        />
                                                    </div>
                                                </div>
                                                
                                                {/* Price & Duration */}
                                                <div className="grid grid-cols-2 gap-4">
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                                            Price ($) <span className="required-asterisk">*</span>
                                                        </label>
                                                        <input
                                                            type="number"
                                                            name="price"
                                                            step="0.01"
                                                            min="0"
                                                            defaultValue={editingService?.price || ''}
                                                            required
                                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent-ink)]"
                                                            placeholder="0.00"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                                            Duration (minutes) <span className="required-asterisk">*</span>
                                                        </label>
                                                        <input
                                                            type="number"
                                                            name="duration"
                                                            min="1"
                                                            defaultValue={editingService?.duration || ''}
                                                            required
                                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent-ink)]"
                                                            placeholder="30"
                                                        />
                                                    </div>
                                                </div>
                                                
                                                {/* Vehicle Type */}
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Vehicle Type
                                                    </label>
                                                    <input
                                                        type="text"
                                                        name="vehicleType"
                                                        defaultValue={editingService?.vehicleType || ''}
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent-ink)]"
                                                        placeholder="e.g., Sedan, SUV, Luxury Sedan"
                                                    />
                                                </div>
                                                
                                                {/* Description */}
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Description
                                                    </label>
                                                    <textarea
                                                        name="description"
                                                        rows="2"
                                                        defaultValue={editingService?.description || ''}
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent-ink)]"
                                                        placeholder="Brief description of the service"
                                                    />
                                                </div>
                                                
                                                {/* Additional Trip Information Template */}
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Additional Trip Information Template
                                                    </label>
                                                    <textarea
                                                        name="additionalInfo"
                                                        rows="6"
                                                        defaultValue={editingService?.additionalInfo || ''}
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent-ink)] text-sm font-mono"
                                                        placeholder="POTENTIAL ADDITIONAL FEES&#10;STANDBY/WAIT: @$1.6667/min&#10;ADDITIONAL STOP(S): @$10.00/Stop"
                                                    />
                                                    <p className="text-xs text-gray-500 mt-1">
                                                        This text will auto-fill in {accountSettings.appointmentType.toLowerCase()} forms
                                                    </p>
                                                </div>
                                                
                                                {/* Active Status */}
                                                <div>
                                                    <label className="flex items-center gap-2 cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            name="active"
                                                            defaultChecked={editingService?.active !== false}
                                                            className="w-4 h-4 text-[var(--accent-ink)] rounded focus:ring-[var(--accent-ink)]"
                                                        />
                                                        <span className="text-sm font-medium text-gray-700">Active (available for {accountSettings.appointmentType.toLowerCase()}s)</span>
                                                    </label>
                                                </div>
                                            </div>
                                            
                                            {/* Actions */}
                                            <div className="flex justify-end gap-3 mt-6">
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setShowServiceModal(false);
                                                        setEditingService(null);
                                                    }}
                                                    className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg font-medium"
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    type="submit"
                                                    className="px-4 py-2 bg-[var(--btn-fill)] text-[var(--btn-ink)] rounded-lg hover:bg-[var(--btn-fill-hover)] font-medium"
                                                >
                                                    {editingService ? 'Update' : 'Add'} Service
                                                </button>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
                
                {/* Products Tab - Full CRUD Implementation */}
                {activeTab === 'products' && (
                    <div>
                        <div className="flex justify-between items-center mb-6">
                            <div>
                                <h3 className="text-2xl font-bold text-gray-800">Products</h3>
                                <p className="text-sm text-gray-600 mt-1">Manage products offered to clients</p>
                            </div>
                            <button
                                onClick={() => {
                                    setEditingProduct(null);
                                    setShowProductModal(true);
                                }}
                                className="px-4 py-2 bg-[var(--btn-fill)] text-[var(--btn-ink)] rounded-lg hover:bg-[var(--btn-fill-hover)] font-medium flex items-center gap-2"
                            >
                                <span>+</span> Add Product
                            </button>
                        </div>
                        
                        {/* Products List */}
                        <div className="bg-white rounded-lg shadow overflow-hidden">
                            <table className="w-full">
                                <thead className="bg-gray-50 border-b">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Name</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Category</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Price</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Status</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {accountSettings.products.map(product => (
                                        <tr key={product.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4">
                                                <div className="font-medium text-gray-900">{product.name}</div>
                                                <div className="text-sm text-gray-500 truncate max-w-xs">{product.description}</div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className="text-sm text-gray-600">{product.category}</span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className="font-medium text-gray-900">${product.price}</span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className={`px-2 py-1 text-xs font-medium rounded ${
                                                    product.active 
                                                        ? 'bg-green-100 text-green-800' 
                                                        : 'bg-red-100 text-red-800'
                                                }`}>
                                                    {product.active ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex gap-2">
                                                    <button
                                                        onClick={() => {
                                                            setEditingProduct(product);
                                                            setShowProductModal(true);
                                                        }}
                                                        className="text-blue-600 hover:text-blue-800 font-medium text-sm"
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        onClick={() => deleteProduct(product.id)}
                                                        className="text-red-600 hover:text-red-800 font-medium text-sm"
                                                    >
                                                        Delete
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            
                            {accountSettings.products.length === 0 && (
                                <div className="text-center py-12">
                                    <p className="text-gray-500">No products yet. Click "Add Product" to get started.</p>
                                </div>
                            )}
                        </div>
                        
                        {/* Add/Edit Product Modal */}
                        {showProductModal && (
                            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowProductModal(false)}>
                                <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                                    <div className="p-6">
                                        <h3 className="text-xl font-bold text-gray-800 mb-4">
                                            {editingProduct ? 'Edit Product' : 'Add New Product'}
                                        </h3>
                                        
                                        <form onSubmit={(e) => {
                                            e.preventDefault();
                                            const formData = new FormData(e.target);
                                            const productData = {
                                                name: formData.get('name'),
                                                category: formData.get('category'),
                                                price: parseFloat(formData.get('price')),
                                                description: formData.get('description'),
                                                active: formData.get('active') === 'on'
                                            };
                                            
                                            if (editingProduct) {
                                                updateProduct({ ...editingProduct, ...productData });
                                            } else {
                                                addProduct(productData);
                                            }
                                            setShowProductModal(false);
                                            setEditingProduct(null);
                                        }}>
                                            <div className="space-y-4">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Product Name</label>
                                                    <input
                                                        type="text"
                                                        name="name"
                                                        defaultValue={editingProduct?.name || ''}
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                                        required
                                                    />
                                                </div>
                                                
                                                <div className="grid grid-cols-2 gap-4">
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                                                        <input
                                                            type="text"
                                                            name="category"
                                                            defaultValue={editingProduct?.category || ''}
                                                            placeholder="e.g., Accessories, Gifts"
                                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                                            required
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">Price ($)</label>
                                                        <input
                                                            type="number"
                                                            name="price"
                                                            step="0.01"
                                                            min="0"
                                                            defaultValue={editingProduct?.price || ''}
                                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                                            required
                                                        />
                                                    </div>
                                                </div>
                                                
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                                                    <textarea
                                                        name="description"
                                                        rows="3"
                                                        defaultValue={editingProduct?.description || ''}
                                                        placeholder="Describe the product..."
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                                    />
                                                </div>
                                                
                                                <div className="flex items-center gap-2">
                                                    <input
                                                        type="checkbox"
                                                        name="active"
                                                        id="productActive"
                                                        defaultChecked={editingProduct?.active !== false}
                                                        className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                                                    />
                                                    <label htmlFor="productActive" className="text-sm font-medium text-gray-700">
                                                        Active (available for selection)
                                                    </label>
                                                </div>
                                            </div>
                                            
                                            <div className="flex gap-3 mt-6">
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setShowProductModal(false);
                                                        setEditingProduct(null);
                                                    }}
                                                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    type="submit"
                                                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                                                >
                                                    {editingProduct ? 'Update' : 'Add'} Product
                                                </button>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
                
                {/* Calendar Tab - Full Implementation */}
                {activeTab === 'calendar' && (
                    <div className="max-w-3xl">
                        <h3 className="text-2xl font-bold text-gray-800 mb-6">Calendar Settings</h3>
                        
                        <div className="space-y-6">
                            {/* Time Scale */}
                            <div className="bg-white rounded-lg shadow p-6">
                                <h4 className="font-semibold text-gray-800 mb-4">Time Scale</h4>
                                <p className="text-sm text-gray-600 mb-4">Select the time interval for calendar views</p>
                                <div className="flex gap-4">
                                    <label className={`flex-1 flex items-center justify-center p-4 border-2 rounded-lg cursor-pointer transition ${
                                        accountSettings.calendar.timeScale === 15 ? 'border-[var(--nav-active-edge)] bg-[var(--nav-active-bg)]' : 'border-gray-200 hover:bg-gray-50'
                                    }`}>
                                        <input
                                            type="radio"
                                            name="timeScale"
                                            value="15"
                                            checked={accountSettings.calendar.timeScale === 15}
                                            onChange={() => updateCalendarSettings({ timeScale: 15 })}
                                            className="w-4 h-4 text-[var(--accent-ink)] mr-3"
                                        />
                                        <span className="font-medium">15 minutes</span>
                                    </label>
                                    <label className={`flex-1 flex items-center justify-center p-4 border-2 rounded-lg cursor-pointer transition ${
                                        accountSettings.calendar.timeScale === 30 ? 'border-[var(--nav-active-edge)] bg-[var(--nav-active-bg)]' : 'border-gray-200 hover:bg-gray-50'
                                    }`}>
                                        <input
                                            type="radio"
                                            name="timeScale"
                                            value="30"
                                            checked={accountSettings.calendar.timeScale === 30}
                                            onChange={() => updateCalendarSettings({ timeScale: 30 })}
                                            className="w-4 h-4 text-[var(--accent-ink)] mr-3"
                                        />
                                        <span className="font-medium">30 minutes</span>
                                    </label>
                                </div>
                            </div>
                            
                            {/* First Day of Week */}
                            <div className="bg-white rounded-lg shadow p-6">
                                <h4 className="font-semibold text-gray-800 mb-4">First Day of Week</h4>
                                <p className="text-sm text-gray-600 mb-4">Choose which day starts your week</p>
                                <div className="flex gap-4">
                                    <label className={`flex-1 flex items-center justify-center p-4 border-2 rounded-lg cursor-pointer transition ${
                                        accountSettings.calendar.firstDayOfWeek === 'Sunday' ? 'border-[var(--nav-active-edge)] bg-[var(--nav-active-bg)]' : 'border-gray-200 hover:bg-gray-50'
                                    }`}>
                                        <input
                                            type="radio"
                                            name="firstDayOfWeek"
                                            value="Sunday"
                                            checked={accountSettings.calendar.firstDayOfWeek === 'Sunday'}
                                            onChange={() => updateCalendarSettings({ firstDayOfWeek: 'Sunday' })}
                                            className="w-4 h-4 text-[var(--accent-ink)] mr-3"
                                        />
                                        <span className="font-medium">Sunday</span>
                                    </label>
                                    <label className={`flex-1 flex items-center justify-center p-4 border-2 rounded-lg cursor-pointer transition ${
                                        accountSettings.calendar.firstDayOfWeek === 'Monday' ? 'border-[var(--nav-active-edge)] bg-[var(--nav-active-bg)]' : 'border-gray-200 hover:bg-gray-50'
                                    }`}>
                                        <input
                                            type="radio"
                                            name="firstDayOfWeek"
                                            value="Monday"
                                            checked={accountSettings.calendar.firstDayOfWeek === 'Monday'}
                                            onChange={() => updateCalendarSettings({ firstDayOfWeek: 'Monday' })}
                                            className="w-4 h-4 text-[var(--accent-ink)] mr-3"
                                        />
                                        <span className="font-medium">Monday</span>
                                    </label>
                                </div>
                            </div>
                            
                            {/* Additional Options */}
                            <div className="bg-white rounded-lg shadow p-6">
                                <h4 className="font-semibold text-gray-800 mb-4">Display / Accessibility</h4>
                                <div className="space-y-4">
                                    <label className="flex items-center gap-3 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={accountSettings.calendar.showWeekNumbers}
                                            onChange={(e) => updateCalendarSettings({ showWeekNumbers: e.target.checked })}
                                            className="w-4 h-4 text-[var(--accent-ink)] rounded focus:ring-[var(--accent-ink)]"
                                        />
                                        <div>
                                            <div className="font-medium text-gray-900">Show Week Numbers</div>
                                            <div className="text-sm text-gray-600">Display week numbers in calendar views</div>
                                        </div>
                                    </label>
                                    
                                    <label className="flex items-center gap-3 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={accountSettings.calendar.useDynamicColumnWidths}
                                            onChange={(e) => updateCalendarSettings({ useDynamicColumnWidths: e.target.checked })}
                                            className="w-4 h-4 text-[var(--accent-ink)] rounded focus:ring-[var(--accent-ink)]"
                                        />
                                        <div>
                                            <div className="font-medium text-gray-900">Use Dynamic Column Widths</div>
                                            <div className="text-sm text-gray-600">Automatically adjust column sizes based on content</div>
                                        </div>
                                    </label>
                                </div>
                            </div>
                            
                            {/* Time Format */}
                            <div className="bg-white rounded-lg border border-gray-200 p-6">
                                <h4 className="font-semibold text-gray-800 mb-4">Time Format</h4>
                                <div className="space-y-3">
                                    <label className="flex items-center gap-3 cursor-pointer">
                                        <input
                                            type="radio"
                                            name="timeFormat"
                                            checked={accountSettings.calendar.timeFormat === '12hr'}
                                            onChange={() => updateCalendarSettings({ timeFormat: '12hr' })}
                                            className="w-4 h-4 text-[var(--accent-ink)] focus:ring-[var(--accent-ink)]"
                                        />
                                        <div>
                                            <div className="font-medium text-gray-900">12-Hour Format (2:30 PM)</div>
                                            <div className="text-sm text-gray-600">Display times with AM/PM</div>
                                        </div>
                                    </label>
                                    
                                    <label className="flex items-center gap-3 cursor-pointer">
                                        <input
                                            type="radio"
                                            name="timeFormat"
                                            checked={accountSettings.calendar.timeFormat === '24hr'}
                                            onChange={() => updateCalendarSettings({ timeFormat: '24hr' })}
                                            className="w-4 h-4 text-[var(--accent-ink)] focus:ring-[var(--accent-ink)]"
                                        />
                                        <div>
                                            <div className="font-medium text-gray-900">24-Hour Format (14:30)</div>
                                            <div className="text-sm text-gray-600">Display times in military format</div>
                                        </div>
                                    </label>
                                </div>
                            </div>
                            
                            {/* ============================================ */}
                            {/* PHASE 2: COLOR BY SETTING */}
                            {/* ============================================ */}
                            <div className="bg-white rounded-lg shadow p-6 border-2 border-green-200">
                                <div className="flex items-center gap-2 mb-4">
                                    <span className="text-xl">🎨</span>
                                    <h4 className="font-semibold text-gray-800">Color Appointments By</h4>
                                    <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded-full">PHASE 1</span>
                                </div>
                                <p className="text-sm text-gray-600 mb-4">Choose how appointments are color-coded in the calendar</p>
                                <div className="flex gap-4">
                                    <label className={`flex-1 flex items-center justify-center p-4 border-2 rounded-lg cursor-pointer transition ${
                                        accountSettings.calendar.colorBy === 'staff' ? 'border-[var(--nav-active-edge)] bg-[var(--nav-active-bg)]' : 'border-gray-200 hover:bg-gray-50'
                                    }`}>
                                        <input
                                            type="radio"
                                            name="colorBy"
                                            value="staff"
                                            checked={accountSettings.calendar.colorBy === 'staff'}
                                            onChange={() => updateCalendarSettings({ colorBy: 'staff' })}
                                            className="w-4 h-4 text-[var(--accent-ink)] mr-3"
                                        />
                                        <div>
                                            <div className="font-medium">By Staff</div>
                                            <div className="text-xs text-gray-600">Each staff member has their own color</div>
                                        </div>
                                    </label>
                                    <label className={`flex-1 flex items-center justify-center p-4 border-2 rounded-lg cursor-pointer transition ${
                                        accountSettings.calendar.colorBy === 'service' ? 'border-[var(--nav-active-edge)] bg-[var(--nav-active-bg)]' : 'border-gray-200 hover:bg-gray-50'
                                    }`}>
                                        <input
                                            type="radio"
                                            name="colorBy"
                                            value="service"
                                            checked={accountSettings.calendar.colorBy === 'service'}
                                            onChange={() => updateCalendarSettings({ colorBy: 'service' })}
                                            className="w-4 h-4 text-[var(--accent-ink)] mr-3"
                                        />
                                        <div>
                                            <div className="font-medium">By Service</div>
                                            <div className="text-xs text-gray-600">Each service type has its own color</div>
                                        </div>
                                    </label>
                                </div>
                            </div>
                            
                            {/* ============================================ */}
                            {/* PHASE 2: CAPACITY MANAGEMENT */}
                            {/* ============================================ */}
                            <div className="bg-white rounded-lg shadow p-6 border-2 border-green-200">
                                <div className="flex items-center gap-2 mb-4">
                                    <span className="text-xl">📊</span>
                                    <h4 className="font-semibold text-gray-800">Capacity Management</h4>
                                    <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded-full">PHASE 1</span>
                                </div>
                                <p className="text-sm text-gray-600 mb-4">Set maximum appointments per hour to manage workload</p>
                                
                                <div className="space-y-4">
                                    <label className="flex items-center gap-3 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={accountSettings.calendar.capacityEnabled || false}
                                            onChange={(e) => updateCalendarSettings({ capacityEnabled: e.target.checked })}
                                            className="w-4 h-4 text-[var(--accent-ink)] rounded focus:ring-[var(--accent-ink)]"
                                        />
                                        <div>
                                            <div className="font-medium text-gray-900">Enable Capacity Limits</div>
                                            <div className="text-sm text-gray-600">Warn when booking too many appointments in one hour</div>
                                        </div>
                                    </label>
                                    
                                    {accountSettings.calendar.capacityEnabled && (
                                        <div className="ml-7 pl-4 border-l-2 border-[var(--nav-active-edge)]">
                                            <label className="block">
                                                <span className="text-sm font-medium text-gray-700 mb-2 block">
                                                    Maximum Appointments Per Hour
                                                </span>
                                                <div className="flex items-center gap-3">
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        max="10"
                                                        value={accountSettings.calendar.maxAppointmentsPerHour || 3}
                                                        onChange={(e) => updateCalendarSettings({ maxAppointmentsPerHour: parseInt(e.target.value) })}
                                                        className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent-ink)]"
                                                    />
                                                    <span className="text-sm text-gray-600">appointments</span>
                                                </div>
                                            </label>
                                        </div>
                                    )}
                                </div>
                            </div>
                            
                            {/* Success Message */}
                            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                <div className="flex items-center gap-2 text-green-800">
                                    <span className="text-lg">✓</span>
                                    <span className="text-sm font-medium">Settings are saved automatically</span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                
                {/* Templates Tab */}
                {activeTab === 'templates' && (
                    <div className="max-w-4xl">
                        <div className="mb-6">
                            <h3 className="text-2xl font-bold text-gray-900 mb-2">⭐ Appointment Templates</h3>
                            <p className="text-gray-600">Save common appointment configurations as templates for faster scheduling</p>
                        </div>
                        
                        {/* Add Template Button */}
                        <div className="mb-6">
                            <button
                                onClick={() => {
                                    setEditingTemplate(null);
                                    setShowTemplateModal(true);
                                }}
                                className="flex items-center gap-2 px-4 py-2 bg-[var(--btn-fill)] text-[var(--btn-ink)] rounded-lg hover:bg-[var(--btn-fill-hover)] transition"
                            >
                                <span className="text-lg">+</span>
                                <span>Create Template</span>
                            </button>
                        </div>
                        
                        {/* Templates List */}
                        {(!accountSettings.appointmentTemplates || accountSettings.appointmentTemplates.length === 0) ? (
                            <div className="bg-gray-50 rounded-lg p-12 text-center border-2 border-dashed border-gray-300">
                                <div className="text-6xl mb-4">📝</div>
                                <h3 className="text-xl font-semibold mb-2 text-gray-800">No Templates Yet</h3>
                                <p className="text-gray-600 mb-4">Create your first template to speed up appointment scheduling</p>
                                <button
                                    onClick={() => {
                                        setEditingTemplate(null);
                                        setShowTemplateModal(true);
                                    }}
                                    className="px-4 py-2 bg-[var(--btn-fill)] text-[var(--btn-ink)] rounded-lg hover:bg-[var(--btn-fill-hover)] transition"
                                >
                                    Create First Template
                                </button>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {accountSettings.appointmentTemplates.map(template => {
                                    const service = accountSettings.services.find(s => s.id === template.serviceId);

                                    return (
                                        <div key={template.id} className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-lg transition">
                                            <div className="flex justify-between items-start mb-3">
                                                <div>
                                                    <h4 className="font-bold text-gray-900 text-lg">{template.name}</h4>
                                                    <p className="text-sm text-gray-600">{service?.name || 'Unknown Service'}</p>
                                                </div>
                                                <div className="flex gap-2">
                                                    <button
                                                        onClick={() => {
                                                            setEditingTemplate(template);
                                                            setShowTemplateModal(true);
                                                        }}
                                                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        onClick={() => deleteTemplate(template.id)}
                                                        className="text-red-600 hover:text-red-800 text-sm font-medium"
                                                    >
                                                        Delete
                                                    </button>
                                                </div>
                                            </div>
                                            
                                            <div className="space-y-2 text-sm">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-gray-500">⏱️ Duration:</span>
                                                    <span className="font-medium text-gray-900">{template.duration} minutes</span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-gray-500">💰 Price:</span>
                                                    <span className="font-medium text-gray-900">${template.defaultPrice?.toFixed(2) || '0.00'}</span>
                                                </div>
                                                {template.notes && (
                                                    <div className="mt-3 pt-3 border-t border-gray-200">
                                                        <p className="text-gray-600 text-xs italic">{template.notes}</p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                        
                        {/* Template Modal */}
                        {showTemplateModal && (
                            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                                <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                                    <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4">
                                        <div className="flex items-center justify-between">
                                            <h2 className="text-2xl font-bold text-gray-800">
                                                {editingTemplate ? '✏️ Edit Template' : '➕ Create Template'}
                                            </h2>
                                            <button onClick={() => setShowTemplateModal(false)} className="text-gray-500 hover:text-gray-700 text-2xl">×</button>
                                        </div>
                                    </div>
                                    <div className="p-6">
                                        <form onSubmit={(e) => {
                                            e.preventDefault();
                                            const formData = new FormData(e.target);
                                            const templateData = {
                                                name: formData.get('name'),
                                                serviceId: parseInt(formData.get('serviceId')),
                                                duration: parseInt(formData.get('duration')),
                                                defaultPrice: parseFloat(formData.get('defaultPrice')),
                                                notes: formData.get('notes')
                                            };
                                            
                                            if (editingTemplate) {
                                                updateTemplate({ ...editingTemplate, ...templateData });
                                            } else {
                                                addTemplate(templateData);
                                            }
                                            setShowTemplateModal(false);
                                            setEditingTemplate(null);
                                        }}>
                                            <div className="space-y-4">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">Template Name *</label>
                                                    <input type="text" name="name" defaultValue={editingTemplate?.name || ''} placeholder="e.g., Standard Airport Transfer" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent-ink)]" required />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">Service *</label>
                                                    <select name="serviceId" defaultValue={editingTemplate?.serviceId || ''} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent-ink)]" required>
                                                        <option value="">Select a service</option>
                                                        {accountSettings.services.map(service => (
                                                            <option key={service.id} value={service.id}>{service.name} - ${service.price}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">Duration (minutes) *</label>
                                                    <input type="number" name="duration" defaultValue={editingTemplate?.duration || 30} min="5" step="5" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent-ink)]" required />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">Default Price *</label>
                                                    <input type="number" name="defaultPrice" defaultValue={editingTemplate?.defaultPrice || 0} min="0" step="0.01" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent-ink)]" required />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">Notes (Optional)</label>
                                                    <textarea name="notes" defaultValue={editingTemplate?.notes || ''} rows="3" placeholder="Add any notes or instructions..." className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent-ink)]" />
                                                </div>
                                            </div>
                                            <div className="flex gap-3 mt-6">
                                                <button type="button" onClick={() => { setShowTemplateModal(false); setEditingTemplate(null); }} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium">Cancel</button>
                                                <button type="submit" className="flex-1 px-4 py-2 bg-[var(--btn-fill)] text-[var(--btn-ink)] rounded-lg hover:bg-[var(--btn-fill-hover)] font-medium">
                                                    {editingTemplate ? 'Update Template' : 'Create Template'}
                                                </button>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}


export default AccountSettingsView;
